import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import heroBg from '../assets/hero-bg.jpg';

const Hero = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkWidth = () => setIsMobile(window.innerWidth < 768);
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const scrollToSection = () => {
    const element = document.getElementById('roi-calculator');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* ✅ HIDDEN IMAGE TAG FOR SEO */}
      <img 
        src={heroBg} 
        alt="Medical billing outsourcing for small practices - Independent physicians using RCM software to reduce claim denials and improve cash flow" 
        style={{ display: 'none' }}
        itemProp="image"
      />

      <div
        style={{
          position: 'relative',
          minHeight: isMobile ? '100vh' : '120vh',
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          overflow: 'hidden',
          padding: isMobile ? '0 5% 40px' : '0 5% 80px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          paddingTop: isMobile ? '80px' : '120px',
        }}
        role="banner"
        aria-label="Hero section for medical billing outsourcing services"
      >
        {/* Dark overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.2))',
            zIndex: 0,
          }}
          aria-hidden="true"
        />

        {/* Headings - SEO OPTIMIZED with NEW KEYWORDS */}
        <div
          ref={ref}
          style={{
            zIndex: 1,
            maxWidth: '800px',
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s ease-out',
            textAlign: isMobile ? 'center' : 'left',
          }}
        >
          {/* ✅ VISIBLE H1 - Primary Keyword */}
          <h1
            style={{
              fontSize: isMobile ? '2rem' : '3.5rem',
              fontWeight: '800',
              lineHeight: isMobile ? '1.2' : '1.2',
              marginBottom: '1.5rem',
              textShadow: '3px 3px 16px rgba(0,0,0,0.8)',
              color: '#ffffff',
            }}
            itemProp="headline"
          >
            Medical Billing Outsourcing for Small Practices
          </h1>

          {/* H2 - Secondary Keywords */}
          <h2
            style={{
              fontSize: isMobile ? '1.3rem' : '2rem',
              fontWeight: '600',
              lineHeight: '1.3',
              marginBottom: '1rem',
              textShadow: '2px 2px 10px rgba(0,0,0,0.7)',
              color: '#ffffff',
            }}
            itemProp="alternativeHeadline"
          >
            Revenue Cycle Management for Independent Physicians & Multispecialty Clinics
          </h2>

          {/* H3 - Supporting Keywords */}
          <h3
            style={{
              fontSize: isMobile ? '1rem' : '1.4rem',
              fontWeight: '500',
              paddingBottom: '0.5rem',
              margin: isMobile ? '0 auto' : 'unset',
              textShadow: '1px 1px 6px rgba(0,0,0,0.6)',
              color: '#f0f0f0',
              lineHeight: '1.5',
            }}
          >
            Reduce Claim Denials in Healthcare Billing by 35-45% with Expert Denial Management Services
          </h3>
        </div>

        {/* ROI Button */}
        <button
          onClick={scrollToSection}
          aria-label="Scroll to ROI Calculator to estimate your medical billing cost savings"
          style={{
            position: isMobile ? 'relative' : 'absolute',
            top: isMobile ? 'auto' : '35%',
            right: isMobile ? 'auto' : '2%',
            marginTop: isMobile ? '40px' : '0',
            alignSelf: isMobile ? 'center' : 'flex-end',
            background: 'linear-gradient(to bottom, #F26522, #d3541c)',
            color: '#fff',
            width: '140px',
            height: '65px',
            textAlign: 'center',
            fontWeight: '600',
            fontSize: '0.85rem',
            lineHeight: '1.2rem',
            paddingTop: '10px',
            border: 'none',
            borderTopLeftRadius: '35px',
            borderTopRightRadius: '35px',
            clipPath: 'polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)',
            boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
            zIndex: 2,
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          ROI CALCULATOR
          <br />
          <span
            style={{
              display: 'inline-block',
              fontSize: '1.4rem',
              animation: 'smoothBounce 2s ease-in-out infinite',
            }}
            aria-hidden="true"
          >
            ▼
          </span>
          <style>
            {`
              @keyframes smoothBounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(8px); }
              }
            `}
          </style>
        </button>

        {/* Mission Box - SEO-OPTIMIZED with NEW KEYWORDS and SERVICE LINKS */}
        <div
          style={{
            zIndex: 1,
            marginTop: isMobile ? '40px' : '120px',
            backgroundColor: 'rgba(0, 25, 45, 0.95)',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '650px',
            fontFamily: "'Poppins', sans-serif",
            alignSelf: isMobile ? 'center' : 'flex-start',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
          itemProp="description"
        >
          {/* ✅ OPTIMIZED: Natural language with new keywords */}
          <p style={{ 
            fontSize: isMobile ? '0.95rem' : '1.05rem', 
            fontWeight: '400', 
            lineHeight: '1.7',
            marginBottom: '1rem',
            color: '#f5f5f5'
          }}>
            <strong>MDeRCM</strong> specializes in <strong>medical billing outsourcing for small practices</strong>, independent physicians, and multispecialty clinics. Our expert team helps you reduce claim denials, accelerate payments, and cut operational costs by 30-50%.
          </p>

          <p style={{ 
            fontSize: isMobile ? '0.9rem' : '1rem', 
            fontWeight: '400', 
            lineHeight: '1.7',
            marginBottom: '1rem',
            color: '#e8e8e8'
          }}>
            We provide comprehensive{' '}
            <Link 
              to="/revenue-cycle-management-independent-physicians" 
              style={{ color: '#00bfff', textDecoration: 'underline' }}
              aria-label="Learn about revenue cycle management for independent physicians"
            >
              revenue cycle management for independent physicians
            </Link>{' '}
            with specialized{' '}
            <Link 
              to="/denial-management-services-medical-clinics" 
              style={{ color: '#00bfff', textDecoration: 'underline' }}
              aria-label="Explore denial management services for medical clinics"
            >
              denial management services for medical clinics
            </Link>{' '}
            to maximize your revenue.
          </p>

          <p style={{ 
            fontSize: isMobile ? '0.9rem' : '1rem', 
            fontWeight: '400', 
            lineHeight: '1.7',
            color: '#e8e8e8'
          }}>
            Our proven strategies help you{' '}
            <Link 
              to="/reduce-claim-denials-healthcare-billing" 
              style={{ color: '#00bfff', textDecoration: 'underline' }}
              aria-label="Learn how to reduce claim denials in healthcare billing"
            >
              reduce claim denials in healthcare billing
            </Link>{' '}
            by 35-45%. Experience the{' '}
            <Link 
              to="/benefits-medical-billing-outsourcing-clinics" 
              style={{ color: '#00bfff', textDecoration: 'underline' }}
              aria-label="Discover benefits of medical billing outsourcing for clinics"
            >
              benefits of medical billing outsourcing
            </Link>{' '}
            with transparent pricing and measurable results.
          </p>

          {/* ✅ SERVICE CTA Links */}
          <div style={{
            marginTop: '25px',
            padding: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '15px',
              color: '#00ffff',
              textAlign: 'center'
            }}>
              🎯 Specialized Solutions By Practice Type
            </h4>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '12px',
              marginTop: '15px'
            }}>
              <Link 
                to="/medical-billing-outsourcing-small-practices"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(0, 119, 182, 0.2)',
                  color: '#ffffff',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(0, 119, 182, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.4)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.2)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
                aria-label="Medical billing outsourcing for small practices"
              >
                <span style={{ marginRight: '8px' }}>👥</span>
                Small Practices
              </Link>

              <Link 
                to="/revenue-cycle-management-independent-physicians"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(0, 119, 182, 0.2)',
                  color: '#ffffff',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(0, 119, 182, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.4)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.2)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
                aria-label="Revenue cycle management for independent physicians"
              >
                <span style={{ marginRight: '8px' }}>👨‍⚕️</span>
                Independent Physicians
              </Link>

              <Link 
                to="/revenue-cycle-management-multispecialty-practices"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(0, 119, 182, 0.2)',
                  color: '#ffffff',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(0, 119, 182, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.4)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.2)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
                aria-label="Revenue cycle management for multispecialty practices"
              >
                <span style={{ marginRight: '8px' }}>🏢</span>
                Multispecialty Clinics
              </Link>

              <Link 
                to="/denial-management-services-medical-clinics"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(0, 119, 182, 0.2)',
                  color: '#ffffff',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(0, 119, 182, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.4)';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 119, 182, 0.2)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
                aria-label="Denial management services for medical clinics"
              >
                <span style={{ marginRight: '8px' }}>🛡️</span>
                Denial Management
              </Link>
            </div>
          </div>

          {/* Additional Specialty Links */}
          <div style={{
            marginTop: '20px',
            display: 'flex',
            gap: '15px',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <Link 
              to="/Dental"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#0077b6',
                color: '#ffffff',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005f8a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0077b6'}
              aria-label="Dental practice medical billing services"
            >
              🦷 Dental Practices
            </Link>
            <Link 
              to="/MentalHealth"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#0077b6',
                color: '#ffffff',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005f8a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0077b6'}
              aria-label="Mental health practice billing solutions"
            >
              🧠 Mental Health
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;